/**
 * AdminUsersPage - Main admin user management interface
 *
 * Feature: 045-admin-user-tool
 * User Story 1: Search and View User Details
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminUsersGraphQL, useAdminUserStatsGraphQL, AdminUserFilters } from '../../graphql/hooks/useAdminUsersGraphQL';
import { UserSearchBar } from '../../components/admin-users/UserSearchBar';
import { UserFilters } from '../../components/admin-users/UserFilters';
import { UserStatsPanel } from '../../components/admin-users/UserStatsPanel';
import { UserListTable } from '../../components/admin-users/UserListTable';
import { adminCreateTestUserUrl } from '../../utils/urls';
import logger from '../../utils/logger';

export function AdminUsersPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState<AdminUserFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  // Combine search query with filters
  const combinedFilters: AdminUserFilters = {
    ...filters,
    search: searchQuery || undefined
  };

  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
    isError: usersIsError,
    refetch: refetchUsers
  } = useAdminUsersGraphQL(page, limit, combinedFilters);

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    isError: statsIsError,
    refetch: refetchStats
  } = useAdminUserStatsGraphQL(combinedFilters);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page on new search
  };

  const handleFilterChange = (newFilters: AdminUserFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  };

  return (
    <div className="p-8 overflow-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">User Management</h1>
            <p className="text-muted mt-1">Manage user accounts and view statistics</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/users/create"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Create User
            </Link>
            <Link
              to={adminCreateTestUserUrl()}
              className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
            >
              Create Test User
            </Link>
          </div>
        </div>

        {/* Statistics Panel */}
        {statsIsError ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-red-500 font-semibold mb-1">Failed to Load Statistics</h3>
                <p className="text-sm text-muted">
                  {statsError instanceof Error ? statsError.message : 'An error occurred while loading user statistics'}
                </p>
              </div>
              <button
                onClick={() => refetchStats()}
                className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : stats ? (
          <UserStatsPanel stats={stats} loading={statsLoading} />
        ) : null}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <UserFilters filters={filters} onChange={handleFilterChange} />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search Bar */}
            <UserSearchBar onSearch={handleSearch} />

            {/* User List */}
            {usersIsError ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-8">
                <div className="text-center">
                  <h3 className="text-red-500 font-semibold text-lg mb-2">Failed to Load Users</h3>
                  <p className="text-muted mb-4">
                    {usersError instanceof Error
                      ? usersError.message
                      : 'An error occurred while loading the user list. This could be due to authentication, network, or server issues.'}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => refetchUsers()}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                      Retry
                    </button>
                    <button
                      onClick={() => {
                        logger.error('AdminUsers', 'Admin Users Error:', usersError);
                        alert('Error details logged to console. Please check browser console for more information.');
                      }}
                      className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
                    >
                      Show Details
                    </button>
                  </div>
                </div>
              </div>
            ) : usersData ? (
              <UserListTable
                users={usersData.users}
                loading={usersLoading}
                page={page}
                limit={limit}
                total={usersData.total}
                onPageChange={setPage}
              />
            ) : usersLoading ? (
              <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
                <p className="text-muted">Loading users...</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
